import { isMobile } from '../utils/device';

const BUBBLE = 0;
const SPARK = 1;
const CLOUD = 2;

interface Particle {
  type: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  baseSize: number;
  life: number;
  maxLife: number;
  hue: number;
  drag: number;
  pulse: number;
  pulseSpeed: number;
}

const BUBBLE_HUES = [175, 180, 188, 195, 160, 150, 270, 310];
const CLOUD_HUES  = [270, 280, 295, 190];
const SPARK_HUES  = [180, 190, 200, 165, 310];

const GRAVITY = 0.015;

export class AcidSmoke {
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private animationId: number | null = null;
  private isRunning = false;
  private rocketElement: HTMLElement | null = null;
  private container!: HTMLElement;
  private lastTs = 0;
  private visibilityHandler?: () => void;
  private resizeHandler?: () => void;

  private readonly mobile: boolean;
  private readonly targetFps: number;
  private readonly maxParticles: number;
  private readonly dprCap: number;
  private readonly tickMs: number;
  private readonly spawnBubbles: number;
  private readonly spawnSparks: number;
  private readonly spawnClouds: number;

  constructor(canvasId = 'acid-smoke-canvas', containerSelector = '.rocket-layer') {
    this.mobile = isMobile();
    const reduced = typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    this.targetFps    = reduced ? 0 : (this.mobile ? 24 : 30);
    this.maxParticles = this.mobile ? 55 : 100;
    this.dprCap       = this.mobile ? 1 : 1.5;
    this.tickMs       = this.targetFps > 0 ? (1000 / this.targetFps) : 1000;

    this.spawnBubbles = this.mobile ? 1 : 2;
    this.spawnSparks  = this.mobile ? 2 : 3;
    this.spawnClouds  = this.mobile ? 0 : 1;

    let canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;

    if (!canvas) {
      const container = document.querySelector(containerSelector) as HTMLElement;
      if (!container) return;
      this.container = container;

      canvas = document.createElement('canvas');
      canvas.id = canvasId;
      canvas.className = 'acid-smoke-canvas';
      Object.assign(canvas.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: '1',
      });
      container.appendChild(canvas);
    } else {
      if (!canvas.parentElement) return;
      this.container = canvas.parentElement;
    }

    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    this.ctx = ctx;
    this.setupCanvas();
    this.rocketElement = document.querySelector('#rocket') as HTMLElement | null;
    this.setupVisibilityPause();
  }

  private setupCanvas() {
    const resize = () => {
      const rect = this.container.getBoundingClientRect();
      const dpr = Math.min(this.dprCap, window.devicePixelRatio || 1);
      this.canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      this.canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      this.canvas.style.width = `${rect.width}px`;
      this.canvas.style.height = `${rect.height}px`;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    this.resizeHandler = resize;
    window.addEventListener('resize', resize);
  }

  private setupVisibilityPause() {
    this.visibilityHandler = () => {
      if (document.hidden) {
        if (this.animationId) { cancelAnimationFrame(this.animationId); this.animationId = null; }
        this.lastTs = 0;
      } else if ((this.isRunning || this.particles.length > 0) && !this.animationId) {
        this.animationId = requestAnimationFrame(this.loop);
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private getRocketPos(): { x: number; y: number } | null {
    if (!this.rocketElement) return null;
    const rr = this.rocketElement.getBoundingClientRect();
    const cr = this.canvas.getBoundingClientRect();
    return { x: rr.left + rr.width / 2 - cr.left, y: rr.bottom - cr.top };
  }

  private pick(arr: readonly number[]): number {
    return arr[(Math.random() * arr.length) | 0];
  }

  private emitBubble(pos: { x: number; y: number }) {
    const angle = (Math.random() - 0.5) * 1.2;
    const speed = 1.2 + Math.random() * 1.6;
    this.particles.push({
      type: BUBBLE,
      x: pos.x + (Math.random() - 0.5) * 40,
      y: pos.y + Math.random() * 8,
      vx: Math.sin(angle) * speed,
      vy: Math.cos(angle) * speed,
      baseSize: 10 + Math.random() * 18,
      size: 0,
      life: 1.0,
      maxLife: 0.8 + Math.random() * 1.0,
      hue: this.pick(BUBBLE_HUES),
      drag: 0.985,
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: 2 + Math.random() * 3,
    });
  }

  private emitSpark(pos: { x: number; y: number }) {
    const angle = (Math.random() - 0.5) * 1.0;
    const speed = 2.5 + Math.random() * 2.5;
    this.particles.push({
      type: SPARK,
      x: pos.x + (Math.random() - 0.5) * 16,
      y: pos.y + Math.random() * 4,
      vx: Math.sin(angle) * speed,
      vy: Math.cos(angle) * speed,
      baseSize: 1.5 + Math.random() * 3,
      size: 0,
      life: 1.0,
      maxLife: 0.2 + Math.random() * 0.4,
      hue: this.pick(SPARK_HUES),
      drag: 0.97,
      pulse: 0,
      pulseSpeed: 0,
    });
  }

  private emitCloud(pos: { x: number; y: number }) {
    const angle = (Math.random() - 0.5) * 0.5;
    const speed = 0.3 + Math.random() * 0.5;
    this.particles.push({
      type: CLOUD,
      x: pos.x + (Math.random() - 0.5) * 30,
      y: pos.y + Math.random() * 10,
      vx: Math.sin(angle) * speed,
      vy: Math.cos(angle) * speed,
      baseSize: 28 + Math.random() * 36,
      size: 0,
      life: 1.0,
      maxLife: 1.5 + Math.random() * 1.0,
      hue: this.pick(CLOUD_HUES),
      drag: 0.992,
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: 1 + Math.random() * 1.5,
    });
  }

  private tick(dt: number) {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    if (this.isRunning) {
      const pos = this.getRocketPos();
      if (pos && this.particles.length < this.maxParticles) {
        for (let i = 0; i < this.spawnClouds; i++) this.emitCloud(pos);
        for (let i = 0; i < this.spawnBubbles; i++) this.emitBubble(pos);
        for (let i = 0; i < this.spawnSparks; i++) this.emitSpark(pos);
      }
    }

    const tScale = Math.min(1.5, dt * 60);

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      const turb = p.type === CLOUD ? 0.04 : p.type === BUBBLE ? 0.12 : 0.08;
      p.vx += (Math.random() - 0.5) * turb * tScale;
      p.vy += GRAVITY * tScale;
      p.vx *= p.drag;
      p.vy *= p.drag;
      p.x += p.vx * tScale;
      p.y += p.vy * tScale;
      p.life -= dt / p.maxLife;
      p.pulse += p.pulseSpeed * dt;
      p.size = p.baseSize * (p.pulseSpeed > 0 ? (1 + Math.sin(p.pulse) * 0.12) : 1);

      if (p.life <= 0) { this.particles.splice(i, 1); }
    }

    const ps = this.particles;
    for (let i = 0; i < ps.length; i++) { if (ps[i].type === CLOUD)  this.drawCloud(ps[i]); }
    for (let i = 0; i < ps.length; i++) { if (ps[i].type === BUBBLE) this.drawBubble(ps[i]); }
    this.ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < ps.length; i++) { if (ps[i].type === SPARK)  this.drawSpark(ps[i]); }
    this.ctx.globalCompositeOperation = 'source-over';
  }

  private drawBubble(p: Particle) {
    const a = Math.max(0, Math.min(1, p.life));
    const { x, y, size, hue } = p;
    const ctx = this.ctx;

    const age = 1 - a;
    const fade = age < 0.1 ? age / 0.1 : (a < 0.2 ? a / 0.2 : 1);
    const o = fade * a;

    ctx.save();

    ctx.globalAlpha = o * 0.35;
    const aura = ctx.createRadialGradient(x, y, size * 0.2, x, y, size * 2.2);
    aura.addColorStop(0,   `hsla(${hue}, 100%, 72%, 0.7)`);
    aura.addColorStop(0.4, `hsla(${hue}, 100%, 58%, 0.3)`);
    aura.addColorStop(0.7, `hsla(${(hue + 30) % 360}, 95%, 50%, 0.1)`);
    aura.addColorStop(1,   `hsla(${hue}, 80%, 40%, 0)`);
    ctx.fillStyle = aura;
    ctx.beginPath();
    ctx.arc(x, y, size * 2.2, 0, Math.PI * 2);
    ctx.fill();

    const lx = -size * 0.25;
    const ly = -size * 0.25;
    ctx.globalAlpha = o * 0.9;
    const body = ctx.createRadialGradient(x + lx, y + ly, size * 0.03, x, y, size);
    body.addColorStop(0,   `hsla(${(hue + 25) % 360}, 100%, 92%, 1)`);
    body.addColorStop(0.15, `hsla(${(hue + 15) % 360}, 100%, 78%, 0.9)`);
    body.addColorStop(0.35, `hsla(${hue}, 100%, 62%, 0.65)`);
    body.addColorStop(0.6, `hsla(${hue}, 100%, 48%, 0.35)`);
    body.addColorStop(0.85, `hsla(${hue}, 95%, 36%, 0.12)`);
    body.addColorStop(1,   `hsla(${hue}, 90%, 25%, 0)`);
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();

    if (!this.mobile) {
      ctx.globalAlpha = o * 0.55;
      ctx.strokeStyle = `hsla(${(hue + 40) % 360}, 100%, 85%, 0.75)`;
      ctx.lineWidth = Math.max(1, size * 0.09);
      ctx.beginPath();
      ctx.arc(x, y, size * 0.88, Math.PI * 0.5, Math.PI * 1.5);
      ctx.stroke();
    }

    const sx = x - size * 0.28;
    const sy = y - size * 0.28;
    const sr = size * 0.32;
    ctx.globalAlpha = o * 0.95;
    const spec = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr);
    spec.addColorStop(0,   'hsla(0, 0%, 100%, 1)');
    spec.addColorStop(0.25, `hsla(${(hue + 20) % 360}, 100%, 92%, 0.8)`);
    spec.addColorStop(0.6, `hsla(${(hue + 10) % 360}, 100%, 80%, 0.3)`);
    spec.addColorStop(1,   `hsla(${hue}, 100%, 65%, 0)`);
    ctx.fillStyle = spec;
    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, Math.PI * 2);
    ctx.fill();

    if (!this.mobile) {
      ctx.globalAlpha = o * 0.35;
      const inner = ctx.createRadialGradient(
        x + size * 0.12, y + size * 0.12, 0,
        x, y, size * 0.55,
      );
      inner.addColorStop(0, `hsla(${(hue + 50) % 360}, 100%, 85%, 0.7)`);
      inner.addColorStop(0.5, `hsla(${(hue + 30) % 360}, 100%, 70%, 0.25)`);
      inner.addColorStop(1, `hsla(${hue}, 95%, 50%, 0)`);
      ctx.fillStyle = inner;
      ctx.beginPath();
      ctx.arc(x, y, size * 0.55, 0, Math.PI * 2);
      ctx.fill();
    }

    const s2x = x + size * 0.18;
    const s2y = y + size * 0.22;
    const s2r = size * 0.12;
    ctx.globalAlpha = o * 0.5;
    const spec2 = ctx.createRadialGradient(s2x, s2y, 0, s2x, s2y, s2r);
    spec2.addColorStop(0, `hsla(${(hue + 40) % 360}, 100%, 95%, 0.9)`);
    spec2.addColorStop(1, `hsla(${(hue + 40) % 360}, 100%, 70%, 0)`);
    ctx.fillStyle = spec2;
    ctx.beginPath();
    ctx.arc(s2x, s2y, s2r, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawSpark(p: Particle) {
    const a = Math.max(0, Math.min(1, p.life));
    const { x, y, size, hue } = p;
    const ctx = this.ctx;
    ctx.save();

    ctx.globalAlpha = a;
    const core = ctx.createRadialGradient(x, y, 0, x, y, size);
    core.addColorStop(0,   'hsla(0, 0%, 100%, 1)');
    core.addColorStop(0.3, `hsla(${hue}, 100%, 88%, 0.9)`);
    core.addColorStop(0.7, `hsla(${hue}, 100%, 65%, 0.4)`);
    core.addColorStop(1,   `hsla(${hue}, 100%, 50%, 0)`);
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = a * 0.6;
    const glow = ctx.createRadialGradient(x, y, size * 0.3, x, y, size * 4);
    glow.addColorStop(0, `hsla(${hue}, 100%, 75%, 0.5)`);
    glow.addColorStop(0.5, `hsla(${(hue + 20) % 360}, 100%, 60%, 0.15)`);
    glow.addColorStop(1, `hsla(${hue}, 100%, 45%, 0)`);
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(x, y, size * 4, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawCloud(p: Particle) {
    const a = Math.max(0, Math.min(1, p.life));
    const { x, y, size, hue } = p;
    const ctx = this.ctx;
    const expanded = size * (1 + (1 - a) * 0.5);

    ctx.save();
    ctx.globalAlpha = a * 0.22;
    const cloud = ctx.createRadialGradient(x, y, 0, x, y, expanded);
    cloud.addColorStop(0,   `hsla(${hue}, 85%, 60%, 0.35)`);
    cloud.addColorStop(0.3, `hsla(${hue}, 75%, 50%, 0.2)`);
    cloud.addColorStop(0.6, `hsla(${(hue + 20) % 360}, 65%, 40%, 0.08)`);
    cloud.addColorStop(1,   `hsla(${hue}, 50%, 28%, 0)`);
    ctx.fillStyle = cloud;
    ctx.beginPath();
    ctx.arc(x, y, expanded, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private loop = (ts: number) => {
    if (!this.isRunning && this.particles.length === 0) {
      this.animationId = null;
      this.lastTs = 0;
      return;
    }
    if (this.targetFps <= 0) { this.animationId = null; return; }

    if (!this.lastTs) this.lastTs = ts;
    const elapsed = ts - this.lastTs;

    if (elapsed >= this.tickMs) {
      this.lastTs = ts;
      this.tick(Math.min(0.05, elapsed / 1000));
    }

    this.animationId = requestAnimationFrame(this.loop);
  };

  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    if (!this.animationId && !document.hidden) {
      this.animationId = requestAnimationFrame(this.loop);
    }
  }

  stop() { this.isRunning = false; }

  clear() {
    this.stop();
    this.particles = [];
    this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  destroy() {
    this.stop();
    if (this.animationId) { cancelAnimationFrame(this.animationId); this.animationId = null; }
    if (this.visibilityHandler) { document.removeEventListener('visibilitychange', this.visibilityHandler); }
    if (this.resizeHandler) { window.removeEventListener('resize', this.resizeHandler); }
    this.particles = [];
    this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
